// Multiplayer Manager for Club Penguin Game
// Renders remote players, their name tags and speech bubbles, and the
// player counter, driven by GameSocketClient events. The server is the
// source of truth: room snapshots reconcile the full remote-player set.

import type { GameSocketClient, PlayerSnapshot } from '../utils/game-websocket-client.js';

interface RemotePlayerView {
  state: PlayerSnapshot;
  container: HTMLElement;
  sprite: HTMLElement;
  bubbleTimer: number | null;
}

interface SpriteUrls {
  idle: string;
  walking: string;
}

const BUBBLE_DURATION_MS = 4000;

const NAME_ADJECTIVES = [
  'Cool',
  'Happy',
  'Swift',
  'Brave',
  'Wise',
  'Kind',
  'Bold',
  'Calm',
  'Fun',
  'Nice',
];
const NAME_ANIMALS = [
  'Penguin',
  'Seal',
  'Fish',
  'Whale',
  'Bird',
  'Fox',
  'Bear',
  'Wolf',
  'Cat',
  'Dog',
];
const NAME_COLORS = [
  '#FF6B6B',
  '#4ECDC4',
  '#45B7D1',
  '#96CEB4',
  '#FECA57',
  '#FF9FF3',
  '#54A0FF',
  '#5F27CD',
  '#00D2D3',
  '#FF9F43',
  '#10AC84',
  '#EE5A24',
  '#0984E3',
  '#6C5CE7',
  '#A29BFE',
];

export class MultiplayerManager {
  private stage: HTMLElement;
  private socket: GameSocketClient;
  private playersContainer: HTMLElement;
  private countDisplay: HTMLElement;
  private players = new Map<string, RemotePlayerView>();
  private sprites: SpriteUrls;

  private localBubble: HTMLElement | null = null;
  private localBubbleTimer: number | null = null;

  constructor(stage: HTMLElement, socket: GameSocketClient, sprites: SpriteUrls) {
    this.stage = stage;
    this.socket = socket;
    this.sprites = sprites;

    this.playersContainer = document.createElement('div');
    this.playersContainer.className = 'remote-players';
    this.stage.appendChild(this.playersContainer);

    this.countDisplay = document.createElement('div');
    this.countDisplay.className = 'player-count';
    this.countDisplay.textContent = 'Players: 1';
    document.body.appendChild(this.countDisplay);

    this.socket.on('room-snapshot', data => this.applySnapshot(data.players));
    this.socket.on('player-joined', player => this.upsertPlayer(player));
    this.socket.on('player-left', data => this.removePlayer(data.playerId));
    this.socket.on('player-moved', player => this.upsertPlayer(player));
    this.socket.on('chat-message', data => this.showBubble(data.playerId, data.message));
    this.socket.on('room-count', data => {
      this.countDisplay.textContent = `Players: ${Math.max(1, data.count)}`;
    });
  }

  public setSprites(sprites: SpriteUrls): void {
    this.sprites = sprites;
    for (const view of this.players.values()) {
      this.applySpriteState(view);
    }
  }

  /** Clear all remote players, e.g. when changing rooms before the new snapshot arrives. */
  public reset(): void {
    for (const view of this.players.values()) {
      this.disposeView(view);
    }
    this.players.clear();
    this.countDisplay.textContent = 'Players: 1';
  }

  public destroy(): void {
    this.reset();
    this.hideLocalBubble();
    this.playersContainer.remove();
    this.countDisplay.remove();
  }

  // --- Local speech bubble (the local penguin element gets flipped, so its
  // bubble lives on the stage and is position-synced from the game loop) ---

  public showLocalBubble(message: string, x: number, y: number): void {
    this.hideLocalBubble();
    this.localBubble = createBubble(message);
    this.stage.appendChild(this.localBubble);
    this.syncLocalBubble(x, y);
    this.localBubbleTimer = window.setTimeout(() => this.hideLocalBubble(), BUBBLE_DURATION_MS);
  }

  public syncLocalBubble(x: number, y: number): void {
    if (this.localBubble) {
      this.localBubble.style.left = `${x}px`;
      this.localBubble.style.top = `${y - 40}px`;
    }
  }

  private hideLocalBubble(): void {
    if (this.localBubbleTimer !== null) {
      clearTimeout(this.localBubbleTimer);
      this.localBubbleTimer = null;
    }
    this.localBubble?.remove();
    this.localBubble = null;
  }

  // --- Remote players ---

  private applySnapshot(players: PlayerSnapshot[]): void {
    const seen = new Set<string>();
    for (const player of players) {
      if (player.playerId === this.socket.getPlayerId()) continue;
      seen.add(player.playerId);
      this.upsertPlayer(player);
    }
    for (const playerId of this.players.keys()) {
      if (!seen.has(playerId)) {
        this.removePlayer(playerId);
      }
    }
  }

  private upsertPlayer(state: PlayerSnapshot): void {
    if (state.playerId === this.socket.getPlayerId()) return;

    let view = this.players.get(state.playerId);
    if (!view) {
      view = this.createView(state);
      this.players.set(state.playerId, view);
    }
    view.state = state;
    view.container.style.left = `${state.x}px`;
    view.container.style.top = `${state.y}px`;
    this.applySpriteState(view);
  }

  private removePlayer(playerId: string): void {
    const view = this.players.get(playerId);
    if (!view) return;
    this.players.delete(playerId);

    view.container.classList.add('leaving');
    window.setTimeout(() => this.disposeView(view), 300);
  }

  private createView(state: PlayerSnapshot): RemotePlayerView {
    const color = pickByHash(state.playerId, NAME_COLORS);

    const container = document.createElement('div');
    container.className = 'remote-player';

    const sprite = document.createElement('div');
    sprite.className = 'remote-player-sprite entering';
    sprite.style.filter = `drop-shadow(0 0 4px ${color}) hue-rotate(${hashCode(state.playerId) % 360}deg) saturate(1.2)`;
    container.appendChild(sprite);

    const nameLabel = document.createElement('div');
    nameLabel.className = 'remote-player-name';
    nameLabel.textContent = displayName(state.playerId);
    nameLabel.style.borderColor = color;
    container.appendChild(nameLabel);

    this.playersContainer.appendChild(container);
    return { state, container, sprite, bubbleTimer: null };
  }

  private applySpriteState(view: RemotePlayerView): void {
    const { sprite, state } = view;
    sprite.style.backgroundImage = `url("${state.isWalking ? this.sprites.walking : this.sprites.idle}")`;
    sprite.style.transform =
      state.isWalking && state.direction === 'left' ? 'scaleX(-1)' : 'scaleX(1)';
  }

  private showBubble(playerId: string, message: string): void {
    const view = this.players.get(playerId);
    if (!view) return;

    view.container.querySelector('.speech-bubble')?.remove();
    if (view.bubbleTimer !== null) {
      clearTimeout(view.bubbleTimer);
    }

    const bubble = createBubble(message);
    bubble.classList.add('attached');
    view.container.appendChild(bubble);
    view.bubbleTimer = window.setTimeout(() => {
      bubble.remove();
      view.bubbleTimer = null;
    }, BUBBLE_DURATION_MS);
  }

  private disposeView(view: RemotePlayerView): void {
    if (view.bubbleTimer !== null) {
      clearTimeout(view.bubbleTimer);
    }
    view.container.remove();
  }
}

function createBubble(message: string): HTMLElement {
  const bubble = document.createElement('div');
  bubble.className = 'speech-bubble';
  bubble.textContent = message;
  return bubble;
}

function hashCode(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = value.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash);
}

function pickByHash(value: string, options: string[]): string {
  return options[hashCode(value) % options.length];
}

function displayName(playerId: string): string {
  const hash = hashCode(playerId);
  const adjective = NAME_ADJECTIVES[hash % NAME_ADJECTIVES.length];
  const animal = NAME_ANIMALS[(hash >> 8) % NAME_ANIMALS.length];
  return `${adjective} ${animal} ${(hash % 999) + 1}`;
}
